import type { Tagged } from 'type-fest';
import type { Account } from '../account';
import { MutableEntity } from '../common';
import type { Product } from '../product';
import { Project } from '../project';
import { UniqueId } from '../UniqueId';

// 200万回生成したときの衝突確率が1%
// こちらのサイトの計算で計算 => https://zelark.github.io/nano-id-cc/
const uuidGenerator = UniqueId.GetFactory(8);

export type Group = MutableEntity & {
  id: Group.Id;
  projectId: Project.Id;
  name: string;
  description: string | undefined;
  policy: Group.Policy;
  isProjectAdmin: boolean;
};

export namespace Group {
  export type Id = Tagged<UniqueId, 'groupId'>;
  export namespace Id {
    export const Generate = () => uuidGenerator.generate() as Id;
    export const FromString = (id: string): Id => uuidGenerator.FromString(id) as Id;
  }
  export type Policy = {
    [K in Product.Key]?: Policy.ProductPolicy<K>;
  };
  export namespace Policy {
    type PermissionDef = {
      key: string;
      name: string;
      description: string;
      requires?: readonly string[];
      grantsProductAccess?: boolean;
    };

    const DefaultPermissions = [
      { key: 'isAvailable', name: '利用', description: 'このプロダクトへのアクセスを許可します。' },
      {
        key: 'isAdmin',
        name: '管理権限',
        description: '管理者としての操作（設定変更など）を許可します。',
        requires: ['isAvailable'],
      },
      {
        key: 'hasPiiAccess',
        name: 'PIIアクセス',
        description: '通常業務でのPIIアクセスを許可します。',
        requires: ['isAvailable'],
      },
    ] as const satisfies readonly PermissionDef[];

    const VocPermissions = [
      { key: 'isAvailable', name: 'QANT VoCの閲覧', description: 'QANT VoCの閲覧を許可します。' },
      {
        key: 'isEditor',
        name: 'QANT VoCの編集',
        description: 'VoCの編集を許可します。',
        requires: ['isAvailable'],
      },
      {
        key: 'hasPiiAccess',
        name: 'QANT VoCの生データの閲覧',
        description: 'QANT VoCのインポート時の生データの閲覧を許可します。',
        requires: ['isAvailable'],
      },
      {
        key: 'isAdmin',
        name: 'QANT VoCの管理',
        description: 'QANT VoCの設定の編集を許可します。',
        requires: ['isEditor'],
      },
    ] as const satisfies readonly PermissionDef[];

    const VocExtraPermissions = [
      {
        key: 'useKarteAsMain',
        name: '（基盤移行用）旧環境（KARTE）をメインで使用する',
        description: 'KARTEとQANTの両方のVoCを利用できる場合、ナビゲーションから開くVoCをKARTEにします。',
        requires: ['isAvailable'],
      },
    ] as const satisfies readonly PermissionDef[];

    const SpeakExtraPermissions = [
      {
        key: 'useKarteAsMain',
        name: '（基盤移行用）旧環境（KARTE）をメインで使用する',
        description: 'KARTEとQANTの両方でSpeakを閲覧できる場合に、通常のSpeak導線をKARTEにします。Speakの利用権限は別途必要です。',
        requires: ['isAvailable'],
        grantsProductAccess: false,
      },
    ] as const satisfies readonly PermissionDef[];

    const SpeakPermissions = [
      { key: 'isAvailable', name: 'Speakの閲覧', description: 'Speakの閲覧を許可します。' },
      {
        key: 'isAdmin',
        name: 'Speakの編集',
        description: 'Speakの編集を許可します。',
        requires: ['isAvailable'],
      },
      {
        key: 'hasPiiAccess',
        name: '個人情報データの閲覧',
        description: '個人情報データの閲覧を許可します。',
        requires: ['isAvailable'],
      },
      {
        key: 'piiDataEdit',
        name: '個人情報データの編集',
        description: '個人情報データの編集を許可します。',
        requires: ['hasPiiAccess'],
      },
      {
        key: 'piiDataDownload',
        name: '個人情報データのダウンロード',
        description: '個人情報データのダウンロードを許可します。',
        requires: ['piiDataEdit'],
      },
      {
        key: 'piiDataDelete',
        name: '個人情報データの削除',
        description: '個人情報データの削除を許可します。',
        requires: ['piiDataDownload'],
      },
    ] as const satisfies readonly PermissionDef[];

    // Auth と Speak のデプロイ順に依存せず段階移行できるよう、移行済みかを保存する内部フラグ。
    // 設定画面には表示せず、設定画面での Speak 権限変更時に true にする。
    const SpeakInternalPermissions = [
      {
        key: 'useGranularPiiPermissions',
        name: '個人情報データ権限の個別設定',
        description: '個人情報データの操作権限を個別に判定します。',
      },
    ] as const satisfies readonly PermissionDef[];

    const TabulaPermissions = [
      { key: 'isAvailable', name: '利用', description: 'テーブルの作成・削除、CSVインポートが行えます。' },
    ] as const satisfies readonly PermissionDef[];

    const DebesoPermissions = [
      { key: 'isAvailable', name: '利用', description: '外部連携の設定が行えます。' },
    ] as const satisfies readonly PermissionDef[];

    const PolicyCatalog = {
      knowledge: DefaultPermissions,
      desk: DefaultPermissions,
      neuro: DefaultPermissions,
      troppus: DefaultPermissions,
      voc: [...VocPermissions, ...VocExtraPermissions],
      speak: [...SpeakPermissions, ...SpeakInternalPermissions, ...SpeakExtraPermissions],
      debeso: DebesoPermissions,
      smartFaq: DefaultPermissions,
      tabula: TabulaPermissions,
    } as const satisfies { [K in Product.Key]: readonly PermissionDef[] };

    /** プロダクト側で認可に反映済みの権限定義。Auth の設定画面と利用可否の判定に使う。 */
    export const PermissionCatalog = { ...PolicyCatalog, speak: [...SpeakPermissions, ...SpeakExtraPermissions] } as const;

    export type ProductPolicy<K extends Product.Key = Product.Key> = {
      [P in (typeof PolicyCatalog)[K][number]['key']]: boolean;
    };

    export type Permission = ProductPolicy<Product.Key>;

    export type Props = {
      [K in Product.Key]?: Partial<ProductPolicy<K>>;
    };

    export type ForProduct<K extends Product.Key> = Pick<Policy, K>;
    export type ForProducts<K extends Product.Key> = Pick<Policy, K>;

    const hasOwnKey = (obj: object, key: PropertyKey): boolean => Object.getOwnPropertyDescriptor(obj, key) !== undefined;

    const catalogDefs = (productKey: string): readonly PermissionDef[] | undefined => {
      if (!hasOwnKey(PolicyCatalog, productKey)) return undefined;
      return (PolicyCatalog as Record<string, readonly PermissionDef[]>)[productKey];
    };

    export const ConstructProductPolicy = <K extends Product.Key>(
      productKey: K,
      raw?: Partial<ProductPolicy<K>> | Record<string, boolean | undefined> | null,
    ): ProductPolicy<K> | undefined => {
      if (!raw || typeof raw !== 'object') return undefined;

      const record = raw as Record<string, unknown>;
      const defs = catalogDefs(productKey);
      if (!defs) return undefined;

      const output = {} as ProductPolicy<K>;
      for (const def of defs) {
        (output as Record<string, boolean>)[def.key] = record[def.key] === true;
      }
      return output;
    };

    export const Construct = (props?: Props | null): Policy => {
      if (!props) return {};

      const output: Policy = {};
      for (const [key, raw] of Object.entries(props) as [Product.Key, Props[Product.Key]][]) {
        // Object.entries は key と raw の対応を切るため、generic の K を保ったまま呼べない
        const bag = ConstructProductPolicy(key, raw as never);
        if (bag) (output as Record<Product.Key, typeof bag>)[key] = bag;
      }
      return output;
    };

    const ownProductPolicy = <K extends Product.Key>(policy: Policy, productKey: K): ProductPolicy<K> | undefined => {
      if (!hasOwnKey(policy, productKey)) return undefined;
      return policy[productKey];
    };

    export const PickProduct = <K extends Product.Key>(params: { policy: Policy; productKey: K }): Pick<Policy, K> => {
      const bag = ownProductPolicy(params.policy, params.productKey);
      return (bag ? { [params.productKey]: bag } : {}) as Pick<Policy, K>;
    };

    export const PickProducts = <K extends Product.Key>(params: { policy: Policy; productKeys: readonly K[] }): Pick<Policy, K> => {
      const output = {} as Pick<Policy, K>;
      for (const productKey of params.productKeys) {
        Object.assign(output, PickProduct({ policy: params.policy, productKey }));
      }
      return output;
    };

    const flag = (policy: Policy, productKey: Product.Key, key: string): boolean => {
      const bag = ownProductPolicy(policy, productKey);
      if (!bag) return false;
      return (bag as Record<string, boolean | undefined>)[key] === true;
    };

    /**
     * 指定プロダクトの通常導線を旧環境（KARTE）にする設定を返す。
     *
     * 両環境で閲覧できる場合のグロナビの行き先選択専用。機能・リソース認可では使用しない。
     *
     * @see apps/auth/docs/authorization-consumer-guide.md
     */
    export const UseKarteAsMain = (params: { policy: Policy; productKey: Product.Key }): boolean => {
      return flag(params.policy, params.productKey, 'useKarteAsMain');
    };

    export const IsAdmin = (params: { policy: Policy; productKey: Product.Key }): boolean => {
      return flag(params.policy, params.productKey, 'isAdmin');
    };

    /**
     * 対象プロダクトの公開済み権限キーのうち、利用権限を与えるキーが 1 つでも true なら利用可能。
     */
    export const IsAvailable = (params: { policy: Policy; productKey: Product.Key }): boolean => {
      if (!hasOwnKey(PermissionCatalog, params.productKey)) return false;
      const defs: readonly PermissionDef[] = PermissionCatalog[params.productKey];
      const bag = ownProductPolicy(params.policy, params.productKey);
      if (!bag) return false;
      return defs.some((def) => def.grantsProductAccess !== false && (bag as Record<string, boolean | undefined>)[def.key] === true);
    };

    /**
     * 対象プロダクトの `hasPiiAccess` 権限キーが true かどうかを返す。
     *
     * グロナビ専用。プロダクトの認可では使わない。
     * PII 判定はグループポリシーの PII アクセス相当キーと `AuthInfo.Account.hasActivePiiAccess` を
     * プロダクト側ヘルパーで合成する。
     *
     * @see apps/auth/docs/authorization-consumer-guide.md
     */
    export const HasPiiAccess = (params: { policy: Policy; productKey: Product.Key | string }): boolean => {
      return flag(params.policy, params.productKey as Product.Key, 'hasPiiAccess');
    };
  }

  export type Props = MutableEntity.Props & {
    id: string;
    projectId: string;
    name: string;
    description?: string | null | undefined;
    policy?: Policy.Props | null;
    isProjectAdmin?: boolean;
  };

  export const Construct = (props: Props): Group => {
    return {
      ...MutableEntity.Construct(props),
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      name: props.name,
      description: props.description ?? undefined,
      policy: Policy.Construct(props.policy),
      isProjectAdmin: props.isProjectAdmin ?? false,
    };
  };

  export namespace Create {
    export type Props = {
      projectId: Project.Id;
      name: string;
      description?: string | undefined;
      policy?: Policy.Props | null;
      isProjectAdmin?: boolean;
    };
    export type Params = {
      operatorId: Account.Id;
      props: Props;
    };
  }
  export const Create = ({ operatorId, props }: Create.Params): Group => {
    const id = Id.Generate();
    return {
      ...MutableEntity.Create({ operatorId }),
      id,
      projectId: props.projectId,
      name: props.name,
      description: props.description,
      policy: Policy.Construct(props.policy),
      isProjectAdmin: props.isProjectAdmin ?? false,
    };
  };

  export namespace Is {
    export const ProjectAdmin = (group: Pick<Group, 'isProjectAdmin'>): boolean => group.isProjectAdmin;
  }

  export namespace Update {
    export type UpdatableKey = 'name' | 'description' | 'policy' | 'isProjectAdmin';
    export type UpdateProps = Pick<Group, 'name' | 'policy'> & Partial<Pick<Group, 'description' | 'isProjectAdmin'>>;
    export type Params = {
      operatorId: Account.Id;
      cur: Group;
      updateProps: UpdateProps;
    };
  }
  export const Update = ({ operatorId, cur, updateProps }: Update.Params): Group => {
    return {
      ...cur,
      ...MutableEntity.Update({ cur, operatorId }),
      ...updateProps,
      isProjectAdmin: updateProps.isProjectAdmin ?? cur.isProjectAdmin,
    };
  };
}
